import { useEffect, useState } from 'react';
import Modal from './Modal';
import BrandLogo from './BrandLogo';
import PosSelect from './PosSelect';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { subscribePosBranding, savePosBranding, posFontStack } from '../utils/posUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import {
  subscribePosCustomFonts, uploadPosCustomFont, removePosCustomFont,
  isPosCustomFontKey, posCustomFontId, posCustomFontKey, loadPosCustomFont,
} from '../utils/posCustomFontUtil';
import {
  POS_FONT_CHOICES,
  POS_FONT_DEFAULT,
  POS_CUSTOM_FONT_EXTENSIONS,
  POS_CUSTOM_FONT_TYPES,
  POS_CUSTOM_FONT_MAX_BYTES,
} from '../constants/posConst';
import '../css/Pos.css';

/**
 * Store Details — this client's own logo and typeface.
 *
 * THE LOGO USED TO BE ADMIN-ONLY, because it lived on the client profile and
 * that node is admin-written. It is stored here instead, in the client's own
 * subtree, so a club can change its own mark without asking anyone. The
 * administrator's logo remains the fallback: clearing this one returns to it
 * rather than leaving the till unbranded.
 *
 * THE BUILT-IN TYPEFACES ARE ALREADY ON THE DEVICE. A club can also import
 * its own font file (.ttf, .otf, .woff, .woff2), which is stored with this client and joins the same dropdown;
 * only a till using it downloads the file.
 *
 * @returns {JSX.Element}
 */
function PosStoreDetailsForm() {
  const { config, client, clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [branding, setBranding] = useState(null);
  const [font, setFont] = useState(POS_FONT_DEFAULT);
  const [logoFile, setLogoFile] = useState(null);
  const [clearLogo, setClearLogo] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  // This club's uploaded fonts (names only), and an upload/removal in flight.
  const [customFonts, setCustomFonts] = useState(null);
  const [fontBusy, setFontBusy] = useState('');
  // Whether the preview can draw the chosen uploaded font yet.
  const [previewReady, setPreviewReady] = useState(true);

  useEffect(() => subscribePosCustomFonts(clientId, setCustomFonts), [clientId]);

  // The preview needs an uploaded font's file registered before it can show it.
  useEffect(() => {
    if (!isPosCustomFontKey(font)) {
      setPreviewReady(true);
      return undefined;
    }
    let cancelled = false;
    setPreviewReady(false);
    loadPosCustomFont(clientId, posCustomFontId(font))
      .then(() => { if (!cancelled) setPreviewReady(true); })
      .catch(() => { if (!cancelled) setPreviewReady(true); });
    return () => { cancelled = true; };
  }, [clientId, font]);

  useEffect(() => {
    const stop = subscribePosBranding(clientId, (value) => {
      setBranding((current) => {
        // Seeded once, so the fields are not rewritten under the cursor.
        if (current !== null) return current;
        setFont(value?.fontFamily || POS_FONT_DEFAULT);
        return value || {};
      });
    });
    return stop;
  }, [clientId]);

  // Object URLs leak for the lifetime of the document unless revoked, and this
  // form can have several logos tried in one sitting.
  useEffect(() => () => {
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
  }, [preview]);

  const handlePickLogo = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLogoFile(file);
    setClearLogo(false);
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePosBranding(clientId, { logoFile, clearLogo, fontFamily: font }, staffEmail);
      setLogoFile(null);
      setClearLogo(false);
      setBranding((current) => ({ ...(current || {}), fontFamily: font }));
      setModal({
        type: 'success',
        title: 'Store details saved',
        message: 'These apply to everyone at this till straight away.',
      });
    } catch (error) {
      setModal({
        type: 'error',
        title: 'Could not save',
        message: describeWriteError(error, {
          what: 'these store details',
          path: `posTenants/${clientId}/branding`,
        }),
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Imports a picked font file and selects it. Selecting is not saving: the font
   * only applies to the till once Save Configuration is pressed.
   *
   * @param {Event} event - the file input's change event
   * @returns {Promise<void>}
   */
  const handlePickFont = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setFontBusy('Importing font…');
    try {
      const { key, name } = await uploadPosCustomFont(clientId, file, (customFonts || []).length, staffEmail);
      setFont(key);
      setModal({
        type: 'success',
        title: 'Font imported',
        message: `"${name}" is now in the list and selected. Press Save Configuration to use it on every screen.`,
      });
    } catch (error) {
      setModal({
        type: 'error',
        title: 'Could not import the font',
        message: describeWriteError(error, { what: 'this font', path: `posTenants/${clientId}/customFonts` }),
      });
    } finally {
      setFontBusy('');
    }
  };

  /**
   * Deletes the selected uploaded font after confirming. If it was the font
   * in use, the till goes back to the default typeface.
   *
   * @returns {void}
   */
  const handleRemoveFont = () => {
    const target = (customFonts || []).find((f) => posCustomFontKey(f.id) === font);
    if (!target) return;
    const inUse = branding?.fontFamily === font;
    setModal({
      type: 'warning',
      title: `Remove "${target.name}"?`,
      message: inUse
        ? 'This font is in use. Every screen goes back to the default typeface.'
        : 'It will no longer be offered in the list.',
      confirmText: 'Remove',
      showCancel: true,
      cancelText: 'Keep it',
      onConfirm: async () => {
        setModal(null);
        setFontBusy('Removing font…');
        try {
          if (inUse) {
            await savePosBranding(clientId, { fontFamily: POS_FONT_DEFAULT }, staffEmail);
            setBranding((current) => ({ ...(current || {}), fontFamily: POS_FONT_DEFAULT }));
          }
          await removePosCustomFont(clientId, target, staffEmail);
          setFont(POS_FONT_DEFAULT);
        } catch (error) {
          setModal({
            type: 'error',
            title: 'Could not remove the font',
            message: describeWriteError(error, { what: 'this font', path: `posTenants/${clientId}/customFonts` }),
          });
        } finally {
          setFontBusy('');
        }
      },
    });
  };

  /** Drops unsaved edits by re-seeding from what is stored. @returns {void} */
  const handleReset = () => {
    setBranding(null);
    setLogoFile(null);
    setClearLogo(false);
    setPreview(null);
  };

  if (branding === null || customFonts === null) {
    return <div className="pos-loading">{spinnerRing('Loading store details…')}</div>;
  }

  // What the logo slot should show: a newly picked file, nothing if it is being
  // cleared, this client's own logo, or the administrator's as the fallback.
  const shown = preview || (clearLogo ? null : (branding.logoUrl || client?.logoUrl || null));
  const usingAdminLogo = !preview && !clearLogo && !branding.logoUrl && Boolean(client?.logoUrl);

  // Built-in faces first, then this club's own imports.
  const fontOptions = [
    ...POS_FONT_CHOICES.map(f => ({ key: f.key, label: f.label })),
    ...customFonts.map(f => ({ key: posCustomFontKey(f.id), label: `${f.name} (imported)` })),
  ];
  // A stored choice whose uploaded font has since been removed elsewhere.
  const selectedCustom = customFonts.find((f) => posCustomFontKey(f.id) === font) || null;
  const fontHint = isPosCustomFontKey(font)
    ? (selectedCustom ? `Imported from ${selectedCustom.fileName || 'an imported font file'}` : 'This imported font was removed — the default is shown instead.')
    : POS_FONT_CHOICES.find(f => f.key === font)?.hint;
  const busy = saving || Boolean(fontBusy);

  return (
    <div className="pos-config">
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Store Details</h2>
          <p>
            The mark and the typeface this till wears. Saved changes apply to everyone
            here straight away, including tills already open.
          </p>
        </div>

        <div className="pos-field pos-field-wide">
          <span>Store logo</span>
          <div className="pos-logo-row">
            <BrandLogo src={shown} alt={config?.courtName || 'Store logo'} className="pos-logo-preview" />
            <div className="pos-logo-actions">
              <label className="pos-btn pos-btn-ghost pos-image-pick">
                {shown ? 'Replace logo' : 'Choose logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handlePickLogo}
                  disabled={saving}
                  style={{ display: 'none' }}
                />
              </label>
              {(branding.logoUrl || preview) && (
                <button
                  type="button"
                  className="pos-btn pos-btn-ghost"
                  onClick={() => { setLogoFile(null); setPreview(null); setClearLogo(true); }}
                  disabled={saving}
                >
                  Remove
                </button>
              )}
              <p className="pos-image-hint">
                Compressed to at most 512px and 60KB before it is stored, because every
                till session here downloads it.
              </p>
              {usingAdminLogo && (
                <p className="pos-theme-hint">
                  Currently showing the logo an administrator set. Choose one here to use
                  your own instead.
                </p>
              )}
              {clearLogo && (
                <p className="pos-theme-hint">
                  Saving removes your logo and goes back to the administrator&rsquo;s.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="pos-field pos-store-font">
          <span>Typeface</span>
          <PosSelect
            label="Font"
            options={fontOptions}
            value={font}
            onChange={setFont}
          />
          <span className="pos-theme-hint">{fontHint}</span>

          {/* Import sits right under the list it adds to. */}
          <div className="pos-font-import">
            <label className={`pos-btn pos-btn-ghost pos-image-pick${busy ? ' disabled' : ''}`}>
              Import font
              <input
                type="file"
                accept={[...POS_CUSTOM_FONT_EXTENSIONS, ...POS_CUSTOM_FONT_TYPES.map((type) => type.mime)].join(',')}
                onChange={handlePickFont}
                disabled={busy}
                style={{ display: 'none' }}
              />
            </label>
            {selectedCustom && (
              <button type="button" className="pos-btn pos-btn-danger" onClick={handleRemoveFont} disabled={busy}>
                Remove this font
              </button>
            )}
          </div>
          <span className="pos-theme-hint">
            {POS_CUSTOM_FONT_EXTENSIONS.join(', ')} files, up to {(POS_CUSTOM_FONT_MAX_BYTES / 1024 / 1024).toFixed(1)}MB.
            Imported fonts are added to the list above for this store only.
          </span>
          {fontBusy && <div className="pos-modal-saving">{spinnerRing(fontBusy)}</div>}

          {/* Shown in the face itself, because a font name tells you nothing
              until you see it set. */}
          <div className="pos-font-preview" style={{
              fontFamily: posFontStack(font),
              // Read by the app-wide font override, which would otherwise
              // paint this preview in the SAVED face instead of the chosen one.
              '--pos-preview-font': posFontStack(font),
              opacity: previewReady ? 1 : 0.5,
            }}>
            <strong>{config?.courtName || 'Your store'}</strong>
            <span>Mineral Water &middot; 24 in stock &middot; ₱20</span>
          </div>
          <span className="pos-theme-hint">
            The saved typeface applies to every screen of this store the moment you save.
          </span>
        </div>

        <div className="pos-theme-actions">
          <button type="button" className="pos-btn pos-btn-ghost" onClick={handleReset} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleSave} disabled={busy}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </section>

      <Modal
        isOpen={Boolean(modal)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText={modal?.confirmText || 'OK'}
        showCancel={modal?.showCancel}
        cancelText={modal?.cancelText}
        onConfirm={modal?.onConfirm}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosStoreDetailsForm;
